import React from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { POrderLineWithHeaderDto } from '../../../../../types/purchaseOrderLines';
import { purchaseOrderLinesService } from '../../../../../services/purchaseOrderLinesService';

interface PurchaseOrderLineRowProps {
  line: POrderLineWithHeaderDto;
  showMoreColumns: boolean;
  onClick: () => void;
}

export const PurchaseOrderLineRow: React.FC<PurchaseOrderLineRowProps> = ({
  line,
  showMoreColumns,
  onClick,
}) => {
  // Calculate completion percentage
  const completionPercentage = purchaseOrderLinesService.calculateCompletionPercentage(
    line.quantity,
    line.quantityDelivered
  );

  // Get status color
  const getStatusColor = (status: number) => {
    switch (status) {
      case 0:
        return 'bg-gray-100 text-gray-800';
      case 10:
        return 'bg-blue-100 text-blue-800';
      case 20:
        return 'bg-yellow-100 text-yellow-800';
      case 30:
        return 'bg-orange-100 text-orange-800';
      case 40:
        return 'bg-green-100 text-green-800';
      case 45:
        return 'bg-green-100 text-green-800';
      case 50:
        return 'bg-purple-100 text-purple-800';
      case 60:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get progress bar color
  const getProgressColor = (percentage: number) => {
    if (percentage === 0) return 'bg-gray-300';
    if (percentage < 50) return 'bg-red-500';
    if (percentage < 100) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <tr
      onClick={onClick}
      className="hover:bg-blue-50 cursor-pointer transition-colors"
    >
      {/* Essential columns */}
      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
        {line.orderNoERP || 'N/A'}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
        {line.lineNoERP || line.lineNo}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
        {line.itemNo}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
        {line.description1 || '-'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">
        <div className="flex flex-col">
          <span className="font-medium">{line.supplierNo}</span>
          <span className="text-xs text-gray-500 truncate max-w-[150px]">
            {line.supplierName}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
        {purchaseOrderLinesService.formatQuantity(line.quantity)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
        {purchaseOrderLinesService.formatQuantity(line.quantityDelivered)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900">
        {purchaseOrderLinesService.formatQuantity(line.remainingQuantity)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <div className="flex-1 w-20 bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full ${getProgressColor(completionPercentage)} transition-all`}
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
          <span className="text-xs font-medium text-gray-600 w-10 text-right">
            {completionPercentage}%
          </span>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
        {purchaseOrderLinesService.formatDate(line.orderDate)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
        {purchaseOrderLinesService.formatDate(line.deliveryDate)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span
          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
            line.orderStatus
          )}`}
        >
          {line.orderStatusText}
        </span>
      </td>

      {/* Additional columns (show more) */}
      {showMoreColumns && (
        <>
          <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
            {purchaseOrderLinesService.formatCurrency(line.price)}
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
            {line.discount ? `${line.discount}%` : '-'}
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
            {line.itemUnitTxt || line.itemUnitCode || '-'}
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
            {line.warehouse || '-'}
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-center">
            {line.batchItemYesNo ? (
              <CheckCircle2 className="w-4 h-4 text-green-600 inline" />
            ) : (
              <XCircle className="w-4 h-4 text-gray-300 inline" />
            )}
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-center">
            {line.serialNoItemYesNo ? (
              <CheckCircle2 className="w-4 h-4 text-green-600 inline" />
            ) : (
              <XCircle className="w-4 h-4 text-gray-300 inline" />
            )}
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
            {line.stdLocationName || '-'}
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
            {line.yourRef || '-'}
          </td>
        </>
      )}
    </tr>
  );
};
